/**
 * Leitura de número digitado por gente — a fonte **única** do web.
 *
 * ## Os dois defeitos que este arquivo fecha
 *
 * **1. O ponto decimal virava milhar no frete.** Havia duas implementações da
 * mesma regra e só uma estava certa. O `normalizar` da `LinhaItem` removia o
 * ponto **apenas quando havia vírgula** (`1.234,50`); o `campoParaFrete` do
 * `gerador` removia o ponto **sempre**:
 *
 * ```
 * "1,10" → 1,10     ✅
 * "1.10" → 110,00   ❌ cem vezes maior, em silêncio
 * "0.15" → 15,00    ❌
 * ```
 *
 * Nenhum teste pegou porque todos digitavam vírgula — o ponto cego ficava
 * exatamente onde o defeito morava.
 *
 * **2. A tela ensinava o erro.** `fracaoParaPercentual` formatava com
 * `String(Number(...))`, então o RT aparecia como **`7.5`, com ponto**, ao lado
 * de um frete formatado `1,10`, com vírgula. O app escrevia ponto num campo e
 * punia ponto com 100× no campo vizinho.
 *
 * ## A regra, determinística
 *
 * Sem consultar faixa nem domínio — o parser é testável sozinho:
 *
 * 1. **dois tipos** de separador → o **último** é o decimal; o outro precisa
 *    validar como milhar (`^\d{1,3}(sep\d{3})+$`);
 * 2. **um tipo, uma ocorrência** → **sempre decimal**. Cobre `1.1`, `1,1`,
 *    `1.10`, `1.100`, `1.234`;
 * 3. **um tipo, várias ocorrências** → só passa como milhar com grupos de três
 *    exatos (`1.234.567`); senão **rejeita** (`1.1.0`, `1.10.10`).
 *
 * A regra 2 já existia no repositório, na `LinhaItem` — unificar é adotar a que
 * estava certa, não inventar uma terceira.
 *
 * O preço está declarado: quem cola `1.234` querendo mil duzentos e trinta e
 * quatro recebe 1,234. Num campo de R$/kg ou de % é o que se quer; a
 * alternativa é adivinhar a intenção, e adivinhar errado num campo de dinheiro
 * foi como nasceu o ORC-2026-0007.
 */

/** Sucesso traz o número; falha traz a frase que o vendedor lê. */
export type Leitura = { ok: true; valor: number } | { ok: false; mensagem: string };

/**
 * Espaços que colam junto do número.
 *
 * `\s` do JavaScript já inclui o NÃO-QUEBRÁVEL (U+00A0) e o estreito
 * (U+202F) — este último é o separador de milhar do Excel pt-BR, e é
 * invisível na tela: sem tratá-lo, o campo recusaria um valor que parece
 * perfeito. A largura-zero (U+200B) e a BOM (U+FEFF) entram à mão, porque
 * `\s` não as cobre e elas viajam coladas em texto copiado de página web.
 *
 * Escritos como escape, não como o caractere: um NBSP literal no código-fonte
 * é invisível para quem revisa — e o `no-irregular-whitespace` o recusa.
 */
const ESPACOS = /[\s\u200B\uFEFF]/gu;

/** Sinais que o Unicode escreve diferente do teclado. */
const EQUIVALENTES: ReadonlyArray<readonly [RegExp, string]> = [
  [/[−‒–—―]/gu, '-'], // menos matemático, travessões
  [/，/gu, ','], // vírgula de largura cheia
  [/．/gu, '.'], // ponto de largura cheia
];

/** Um grupo de milhar bem-formado: `1.234`, `12.345.678`. */
const MILHAR = /^\d{1,3}([.,]\d{3})+$/u;

/**
 * Texto digitado → número.
 *
 * Devolve `{ok:false}` com mensagem em vez de `NaN`: quem chama não pode mandar
 * um `NaN` ao servidor sem perceber.
 *
 * A ordem das checagens é fixa e importa — vazio, sinal, caracteres,
 * separadores. Sem ela, `-1,10` seria "formato inválido" em vez de "não pode
 * ser negativo", e a mensagem erraria o alvo.
 */
export function lerDecimalBr(bruto: string): Leitura {
  let texto = bruto;
  for (const [de, para] of EQUIVALENTES) texto = texto.replace(de, para);
  texto = texto.replace(ESPACOS, '');
  /*
   * Lista BRANCA ancorada, não "tudo que não é dígito": remover livremente
   * transformaria `1,10/m²` — que é a unidade ERRADA, e o erro que a Story C
   * veio corrigir — num 110 silencioso.
   */
  texto = texto.replace(/^R\$/u, '').replace(/%$/u, '');

  if (texto === '') return { ok: false, mensagem: 'Informe um número.' };

  let negativo = false;
  if (texto.startsWith('-')) {
    negativo = true;
    texto = texto.slice(1);
  } else if (texto.startsWith('+')) {
    texto = texto.slice(1);
  }
  if (texto === '') return { ok: false, mensagem: 'Informe um número.' };

  // Letra reprovada AQUI, não no `Number` — que aceitaria `0x1f`, `1e3` e
  // ` Infinity ` e devolveria um número que ninguém digitou.
  if (!/^[\d.,]+$/u.test(texto)) return { ok: false, mensagem: 'Número inválido.' };

  const pontos = (texto.match(/\./gu) ?? []).length;
  const virgulas = (texto.match(/,/gu) ?? []).length;

  let normalizado: string;
  if (pontos === 0 && virgulas === 0) {
    normalizado = texto;
  } else if (pontos > 0 && virgulas > 0) {
    // Regra 1 — os dois tipos presentes: o último manda, o outro é milhar.
    const corte = Math.max(texto.lastIndexOf('.'), texto.lastIndexOf(','));
    const inteiro = texto.slice(0, corte);
    const decimal = texto.slice(corte + 1);
    if (!MILHAR.test(inteiro)) return { ok: false, mensagem: 'Confira os pontos e as vírgulas.' };
    normalizado = `${inteiro.replace(/[.,]/gu, '')}.${decimal === '' ? '0' : decimal}`;
  } else if (pontos + virgulas === 1) {
    /*
     * Regra 2 — um separador só: é o decimal. Sempre. É aqui que `1.10` deixa
     * de virar 110.
     */
    const corte = Math.max(texto.lastIndexOf('.'), texto.lastIndexOf(','));
    const inteiro = texto.slice(0, corte);
    const decimal = texto.slice(corte + 1);
    // `1,` é Tab antes dos centavos, não erro — e `1.` já era aceito por
    // acidente. Tratar os dois igual acaba com a assimetria.
    normalizado = `${inteiro === '' ? '0' : inteiro}.${decimal === '' ? '0' : decimal}`;
  } else {
    // Regra 3 — o mesmo separador repetido: só milhar, e estrito.
    if (!MILHAR.test(texto)) return { ok: false, mensagem: 'Confira os pontos e as vírgulas.' };
    normalizado = texto.replace(/[.,]/gu, '');
  }

  if (!/^\d+(\.\d+)?$/u.test(normalizado)) return { ok: false, mensagem: 'Número inválido.' };
  const n = Number(normalizado);
  if (!Number.isFinite(n)) return { ok: false, mensagem: 'Número inválido.' };
  return { ok: true, valor: negativo ? -n : n };
}

/**
 * Número → texto pt-BR, **sem ICU**.
 *
 * `toLocaleString` depende de full-ICU: num runtime sem ele, `pt-BR` cai para
 * en-US e volta a escrever `1.10`. Como esta correção inteira existe para que a
 * tela nunca ensine o ponto decimal, o formatador não pode depender de uma
 * biblioteca que às vezes não está lá.
 *
 * `casas` fixa o **mínimo** de decimais (dinheiro quer 2); o excedente é aparado
 * sem zeros à toa — `7` e não `7,0000` no campo de percentual.
 */
export function numeroBr(valor: number, casas = 0): string {
  if (!Number.isFinite(valor)) return '';
  const negativo = valor < 0;
  const fixo = Math.abs(valor).toFixed(6);
  const partes = fixo.split('.');
  const inteiro = partes[0] ?? '0';
  let decimal = (partes[1] ?? '').replace(/0+$/u, '');
  while (decimal.length < casas) decimal += '0';
  const texto = decimal === '' ? inteiro : `${inteiro},${decimal}`;
  return negativo ? `-${texto}` : texto;
}

/** Faixa de um campo: o que é impossível, e o que é só improvável. */
export interface Faixa {
  /** Abaixo disto é recusado. */
  min: number;
  /** Acima disto é recusado — `maxExclusivo` recusa o próprio `max` também. */
  max: number;
  maxExclusivo?: boolean;
  /** Acima disto passa, mas com ressalva — o dedo-gordo que ainda é número legal. */
  suspeitoAcima?: number;
  /** Abaixo disto passa, mas com ressalva — o `0,07` que queria dizer `7`. */
  suspeitoAbaixo?: number;
  recusa: (valor: number) => string;
  alerta: (valor: number) => string;
  /**
   * A frase que ensina a UNIDADE, colada em toda recusa de formato.
   *
   * "Número inválido." sozinho não diz se o campo quer `7` ou `0,07` — e essa é
   * exatamente a dúvida que produziu o ORC-2026-0007. O parser é genérico; a
   * unidade é do campo, então ela entra aqui.
   */
  dica: string;
}

/** Três desfechos, não dois: aceita, aceita-com-ressalva, recusa. */
export type LeituraNaFaixa =
  { ok: true; valor: number; alerta?: string } | { ok: false; mensagem: string };

export function lerNaFaixa(bruto: string, faixa: Faixa): LeituraNaFaixa {
  const lido = lerDecimalBr(bruto);
  // Recusa de FORMATO leva a unidade junto; a de FAIXA já a traz na frase.
  if (!lido.ok) return { ok: false, mensagem: `${lido.mensagem} ${faixa.dica}` };
  const v = lido.valor;
  const acimaDoTeto = faixa.maxExclusivo === true ? v >= faixa.max : v > faixa.max;
  if (v < faixa.min || acimaDoTeto) return { ok: false, mensagem: faixa.recusa(v) };

  const alto = faixa.suspeitoAcima;
  const baixo = faixa.suspeitoAbaixo;
  if ((alto !== undefined && v > alto) || (baixo !== undefined && v > 0 && v < baixo)) {
    return { ok: true, valor: v, alerta: faixa.alerta(v) };
  }
  return { ok: true, valor: v };
}

/**
 * RT em **percentual** — o vendedor digita `7`, não `0,07`.
 *
 * O teto de 100 é **exclusivo** porque o banco guarda a fração em
 * `numeric(8,6)` e o serviço recusa `>= 1`. Barrar aqui troca um erro de API
 * que fala de "FRACAO" por uma frase na língua de quem digitou.
 *
 * Avisa acima de 15% (o dobro do maior RT praticado) e **abaixo de 1%** — quem
 * digita `0,07` no campo de % quase certamente queria `7`, e é o erro de
 * unidade do ORC-2026-0007 chegando pelo outro lado.
 *
 * `padrao` é o RT do tenant **como o campo o exibe** (`'7'`, `'0'`, `'7,5'`):
 * ele vem do `commercial_param` e pode ser zerado por dado, então a frase não
 * pode cravar 7%. Sem padrão conhecido, a frase sai sem ele. O `7` da dica e
 * do `0,07` é exemplo de unidade, não o padrão.
 */
export function faixaRt(padrao?: string): Faixa {
  const temPadrao = padrao !== undefined && padrao !== '';
  return {
    min: 0,
    max: 100,
    maxExclusivo: true,
    suspeitoAcima: 15,
    suspeitoAbaixo: 1,
    dica: 'Informe um percentual, ex.: 7.',
    recusa: (v) =>
      v < 0
        ? 'O RT não pode ser negativo.'
        : `RT precisa ser menor que 100%.${temPadrao ? ` O padrão é ${padrao}%.` : ''}`,
    alerta: (v) =>
      v < 1
        ? `RT de ${numeroBr(v)}% — para 7%, digite 7 (não 0,07).`
        : `RT de ${numeroBr(v)}% — confira${temPadrao ? `, o padrão é ${padrao}%` : ''}.`,
  };
}

/** A faixa sem padrão conhecido — a frase não cita número nenhum. */
export const FAIXA_RT: Faixa = faixaRt();

/**
 * Frete em **R$/kg** — o vendedor digita `1,10`.
 *
 * Zero é válido e frequente ("frete por conta do cliente"), com teste de
 * integração próprio, então o piso é 0 e não há ressalva em 0.
 *
 * O teto de 100 pega o `110` e o `1100` que eram `1,10`. A ressalva em 10 pega
 * a família que mais dói: frete lançado em **R$/m²** em vez de R$/kg cai entre
 * 17 e 23 — número perfeitamente legal que só o domínio denuncia.
 */
export const FAIXA_FRETE: Faixa = {
  min: 0,
  max: 100,
  suspeitoAcima: 10,
  dica: 'Informe um valor em R$/kg, ex.: 1,10.',
  recusa: (v) =>
    v < 0
      ? 'O frete não pode ser negativo.'
      : 'Frete acima de R$ 100,00/kg. Confira: o padrão é R$ 1,10.',
  alerta: (v) => `Frete de R$ ${numeroBr(v, 2)}/kg — confira, o padrão é R$ 1,10.`,
};

/* ------------------------------------------------- os dois campos, ponta a ponta --- */

/** O que o campo devolve: a string pronta para o PATCH, ou a recusa. */
export type Conversao =
  { ok: true; valor: string; alerta?: string } | { ok: false; mensagem: string };

/**
 * Seis casas — a escala de `numeric(8,6)` e `numeric(12,6)`.
 *
 * `7 / 100` em ponto flutuante dá `0.07000000000000001`; mandar isso ao
 * servidor gravaria um número que não é o que a pessoa digitou.
 */
const ESCALA = 6;

/**
 * Campo de RT (**percentual**) → fração para o PATCH. `'7'` → `'0.070000'`.
 *
 * A divisão por 100 acontece **aqui e só aqui**. Um segundo `÷100` em qualquer
 * outro ponto seria a confusão de unidade do ORC-2026-0007 voltando por outro
 * caminho.
 *
 * `padrao` (opcional) entra só nas frases — ver `faixaRt`.
 */
export function lerRtPercentual(texto: string, padrao?: string): Conversao {
  const r = lerNaFaixa(texto, faixaRt(padrao));
  if (!r.ok) return r;
  const fracao = (r.valor / 100).toFixed(ESCALA);
  return r.alerta === undefined
    ? { ok: true, valor: fracao }
    : { ok: true, valor: fracao, alerta: r.alerta };
}

/** Campo de frete (**R$/kg**) → valor para o PATCH. `'1,10'` → `'1.100000'`. */
export function lerFreteRsKg(texto: string): Conversao {
  const r = lerNaFaixa(texto, FAIXA_FRETE);
  if (!r.ok) return r;
  const valor = r.valor.toFixed(ESCALA);
  return r.alerta === undefined ? { ok: true, valor } : { ok: true, valor, alerta: r.alerta };
}

/**
 * Fração do banco → o percentual que o campo mostra. `'0.075'` → `'7,5'`.
 *
 * Era aqui que a tela escrevia `7.5` com ponto — e ensinava o vendedor a usar
 * a tecla que o campo do lado punia com 100×.
 */
export function fracaoParaPercentual(fracao: string | null | undefined): string {
  if (fracao === null || fracao === undefined || fracao === '') return '';
  const n = Number(fracao);
  if (!Number.isFinite(n)) return '';
  /*
   * O arredondamento mata o resíduo binário antes de formatar: `0.07 × 100` dá
   * 7.000000000000001, e o campo mostraria isso.
   */
  return numeroBr(Number((n * 100).toFixed(4)));
}

/* ------------------------------------------- a comissão: conjunto, não faixa --- */

/** Uma comissão oferecida pelo servidor, já com o preço que ela produz. */
export interface OpcaoComissao {
  /** A chave do banco: `com_4_5`. */
  nivel: string;
  /** O que o vendedor lê: `4,5%`. */
  rotulo: string;
  /** Preço-base da coluna, ou `null` quando ela não tem preço. */
  preco: string | null;
}

/** Três desfechos, como `lerNaFaixa` — mas o terceiro é "existe e não tem preço". */
export type LeituraComissao = { ok: true; nivel: string } | { ok: false; mensagem: string };

/** `4,5%` / `4,5` / `4.5` → `4.5`. `undefined` quando não é número. */
function pctDigitado(bruto: string): number | undefined {
  const limpo = bruto.trim().replace(/%/g, '').trim();
  if (limpo === '') return undefined;
  const r = lerDecimalBr(limpo);
  return r.ok ? r.valor : undefined;
}

/**
 * O campo de comissão: **conjunto discreto**, não faixa.
 *
 * ## Por que não usa `lerNaFaixa`
 *
 * `Faixa` descreve um intervalo contínuo (`min`/`max`), e RT e frete são isso —
 * qualquer número entre 0 e 100 é um RT legítimo. Comissão não: só existem as
 * seis colunas que a fábrica manda, e 4,3% não é "um valor fora da faixa", é um
 * valor que **não tem preço**. Espremer isso num `min`/`max` diria "entre 2 e
 * 5%", que aceitaria 4,3 e mentiria.
 *
 * ## A recusa ENSINA o conjunto
 *
 * É o pedido do cliente levado a sério: ele quis digitar, não escolher de uma
 * lista fechada. Então quando o número não existe, a resposta não pode ser
 * "inválido" — tem de dizer quais existem, ou ele tenta 4,2 e 4,1 no escuro.
 *
 * ## Sem preço é recusa própria
 *
 * Uma coluna pode existir na escala e não ter preço PARA AQUELE PRODUTO. Aí o
 * problema não é o número digitado, e a frase tem de dizer outra coisa — senão
 * o vendedor conclui que digitou errado e tenta de novo igual.
 */
export function lerComissao(bruto: string, opcoes: readonly OpcaoComissao[]): LeituraComissao {
  const disponiveis = opcoes.filter((o) => o.preco !== null);
  const lista = (os: readonly OpcaoComissao[]): string => os.map((o) => o.rotulo).join(', ');

  if (bruto.trim() === '') {
    return {
      ok: false,
      mensagem:
        disponiveis.length === 0
          ? 'Este produto não tem preço em nenhuma comissão desta tabela.'
          : `Informe a comissão. Disponíveis: ${lista(disponiveis)}.`,
    };
  }

  const n = pctDigitado(bruto);
  if (n === undefined) {
    return {
      ok: false,
      mensagem: `Informe um percentual, ex.: 4,5. Disponíveis: ${lista(disponiveis)}.`,
    };
  }

  /*
   * Casa pelo RÓTULO, não por aritmética sobre a chave.
   *
   * O rótulo vem do servidor, da mesma fonte única que a trilha e o seletor
   * usam. Derivar o percentual do nome da chave aqui seria refazer exatamente
   * o defeito do `rotuloNivel` — `del_3` virando 3% quando é 7%.
   *
   * O primeiro percentual do rótulo é a COMISSÃO: `4,5%` nas tabelas sem del e
   * `5,5% · DEL 15%` no del credere, onde o segundo número é outra coisa.
   */
  const alvo = opcoes.find((o) => pctDigitado(o.rotulo.split('·')[0] ?? '') === n);

  if (alvo === undefined) {
    return {
      ok: false,
      mensagem: `${numeroBr(n, n % 1 === 0 ? 0 : 1)}% não tem preço nesta tabela. Disponíveis: ${lista(disponiveis)}.`,
    };
  }

  if (alvo.preco === null) {
    return {
      ok: false,
      mensagem:
        disponiveis.length === 0
          ? `Este produto não tem preço em nenhuma comissão desta tabela.`
          : `Este produto não tem preço em ${alvo.rotulo}. Disponíveis: ${lista(disponiveis)}.`,
    };
  }

  return { ok: true, nivel: alvo.nivel };
}

/**
 * R$/kg do banco → o campo, **com centavos**. `'1.100000'` → `'1,10'`.
 *
 * Duas casas fixas ao contrário do percentual: R$ 1,1 não é como se escreve
 * dinheiro, e o cliente pediu explicitamente que o frete aceitasse 0,15.
 */
export function freteParaCampo(valor: string | null | undefined): string {
  if (valor === null || valor === undefined || valor === '') return '';
  const n = Number(valor);
  if (!Number.isFinite(n)) return '';
  return numeroBr(n, 2);
}
